import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type {
    Certificate,
    Education as EducationRow,
    Experience,
    Project
} from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Education } from '@/components/landing/education'
import { ExperienceTimeline } from '@/components/landing/experience-timeline'
import { Hero } from '@/components/landing/hero'
import { Manifesto } from '@/components/landing/manifesto'
import { Numbers } from '@/components/landing/numbers'
import { SelectedWork } from '@/components/landing/selected-work'
import { Showcase } from '@/components/landing/showcase'
import { Skills } from '@/components/landing/skills'

import {
    getCertificates,
    getEducations,
    getExperiences,
    getProjects
} from '@/lib/data'

interface HomeProps {
    featured: Project[]
    certificates: Certificate[]
    certificateTotal: number
    experiences: Experience[]
    educations: EducationRow[]
}

const Home: FC<HomeProps> = ({ featured = [], certificates = [], certificateTotal = 0, experiences = [], educations = [] }): ReactNode => {
    return (
        <Layout>
            <Seo description='Portofolio Iza Faroza, web developer dan mahasiswa Ilmu Komputer STIKOM El Rahma Bogor. Lihat proyek sistem informasi, website, karya tulis ilmiah, pengalaman organisasi dan sertifikat.' />

            <Hero />
            <Manifesto />
            <SelectedWork projects={featured} />
            <Skills />
            <Showcase certificates={certificates} total={certificateTotal} />
            <Numbers />
            <ExperienceTimeline experiences={experiences} />
            <Education educations={educations} />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    const [projects, certificates, experiences, educations] = await Promise.all([
        getProjects().catch((error: unknown) => {
            console.error('Home projects failed to load:', error instanceof Error ? error.message : error)
            return [] as Project[]
        }),
        getCertificates().catch((error: unknown) => {
            console.error('Home certificates failed to load:', error instanceof Error ? error.message : error)
            return [] as Certificate[]
        }),
        getExperiences().catch((error: unknown) => {
            console.error('Home experiences failed to load:', error instanceof Error ? error.message : error)
            return [] as Experience[]
        }),
        getEducations().catch((error: unknown) => {
            console.error('Home educations failed to load:', error instanceof Error ? error.message : error)
            return [] as EducationRow[]
        })
    ])

    const featured = projects.filter(project => project.featured === 1).slice(0, 6)
    const withImages = certificates.filter(certificate => Boolean(certificate.image))
    const highlighted = [
        ...withImages.filter(certificate => certificate.featured === 1),
        ...withImages.filter(certificate => certificate.featured !== 1)
    ].slice(0, 9)

    return {
        props: {
            featured: featured.length > 0 ? featured : projects.slice(0, 6),
            certificates: highlighted,
            certificateTotal: certificates.length,
            experiences,
            educations
        },
        revalidate: 60
    }
}

export default Home
